import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import KibDivider from '@chewy/kib-divider-react'
import { KibGrid, KibGridItem } from '@chewy/kib-layout-react'

import { MessageStatus } from '~/constants/communications'
import {
  deleteMessage,
  editMessage,
  updateMessage,
} from '~/store/actions/conversationMessages'
import { openIntercom } from '~/store/duck/intercom'
import type { ChatMessage } from '~/types'

import AttachmentRow from '../../AttachmentRow'

import '@chewy/kib-divider-styles/dist/kib-divider.css'
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './MessageActions.module.scss'

const MessageActions = ({
  message,
  readOnly,
}: {
  message: ChatMessage
  readOnly: boolean
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['Common', 'Communications'])
  const { status, id: messageId, isMedical, attachments } = message
  const isNotDeliveredAndActionable =
    status === MessageStatus.FAILED && !readOnly
  const hasFormingFailed = status === MessageStatus.FORMING_FAILED
  const hasFailed = isNotDeliveredAndActionable || hasFormingFailed
  const isDraft = status === MessageStatus.DRAFT
  const showActionButtons = hasFailed || isDraft
  const showAttachments = Boolean(attachments?.length)
  const onSend = () =>
    dispatch(
      updateMessage({
        messageId,
        status: MessageStatus.SENT,
        files: attachments,
        isMedical,
      }),
    )
  const onContactSupport = () => dispatch(openIntercom())
  const onEdit = () => dispatch(editMessage(messageId))
  const onDelete = () => dispatch(deleteMessage(messageId))

  return (
    <>
      {showAttachments && (
        <AttachmentRow
          hideLabel
          ariaLabelledBy={`attachment-group-for-message-${messageId}`}
          attachments={attachments}
        />
      )}
      {showAttachments && showActionButtons && <KibDivider />}
      {showActionButtons && (
        <KibGrid align="right" className={styles.messageFooter}>
          {isNotDeliveredAndActionable && (
            <KibGridItem>
              <KibButtonNew
                emphasis="tertiary"
                size="small"
                theme="utility"
                onClick={onSend}
              >
                {t('Communications:MESSAGE_CARD.SEND_AGAIN')}
              </KibButtonNew>
            </KibGridItem>
          )}
          {hasFormingFailed && (
            <KibGridItem>
              <KibButtonNew
                emphasis="tertiary"
                size="small"
                theme="utility"
                onClick={onContactSupport}
              >
                {t('Common:CONTACT_SUPPORT')}
              </KibButtonNew>
            </KibGridItem>
          )}
          {isDraft && (
            <>
              <KibGridItem>
                <KibButtonNew
                  emphasis="primary"
                  size="small"
                  theme="action"
                  onClick={onEdit}
                >
                  {t('Common:EDIT_ACTION')}
                </KibButtonNew>
              </KibGridItem>
              <KibGridItem>
                <KibButtonNew
                  emphasis="tertiary"
                  size="small"
                  theme="utility"
                  onClick={onDelete}
                >
                  {t('Common:DELETE_ACTION')}
                </KibButtonNew>
              </KibGridItem>
            </>
          )}
        </KibGrid>
      )}
    </>
  )
}

export default MessageActions
